import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { RegHeader } from '../components/regularizacion/RegHeader';
import { RegIntro } from '../components/regularizacion/RegIntro';
import { ViaSelector } from '../components/regularizacion/ViaSelector';
import { Quiz } from '../components/regularizacion/Quiz';
import { QuizResults } from '../components/regularizacion/QuizResults';
import { LeadContactForm } from '../components/regularizacion/LeadContactForm';
import { TrustBadges } from '../components/regularizacion/TrustBadges';
import { evaluate } from '../components/regularizacion/evaluator';
import { leadService } from '../services/leadService';
import { trackTestCompleted, trackViewContent } from '../lib/landing/pili-track';
import { container, btnSecondary, REG_FONTS } from '../components/regularizacion/styles';
import type { Via, StoredAnswer } from '../components/regularizacion/questions';
import type { EvaluationResult } from '../components/regularizacion/evaluator';

type Screen = 'intro' | 'via' | 'quiz' | 'results';

export function RegularizacionLanding() {
  const { t } = useTranslation('regularizacion');
  const { currentLang } = useLanguage();
  const [screen, setScreen] = useState<Screen>('intro');
  const [via, setVia] = useState<Via | null>(null);
  const [answers, setAnswers] = useState<Record<string, StoredAnswer>>({});
  const [result, setResult] = useState<EvaluationResult | null>(null);

  useEffect(() => {
    trackViewContent('regularizacion_evaluator').catch(() => {});
  }, []);

  const handleSelectVia = useCallback((selectedVia: Via) => {
    setVia(selectedVia);
    setAnswers({});
    setScreen('quiz');
  }, []);

  const handleQuizComplete = useCallback(
    (quizAnswers: Record<string, StoredAnswer>) => {
      if (!via) return;
      setAnswers(quizAnswers);
      const evaluation = evaluate(via, quizAnswers, currentLang);
      setResult(evaluation);
      setScreen('results');

      leadService.trackCompletion({ via, eligible: evaluation.eligible }).catch(() => {});
      trackTestCompleted(via, evaluation.eligible).catch(() => {});
    },
    [via, currentLang]
  );

  const handleBackToVia = useCallback(() => {
    setScreen('via');
    setAnswers({});
  }, []);

  const handleNewEval = useCallback(() => {
    setScreen('intro');
    setVia(null);
    setAnswers({});
    setResult(null);
  }, []);

  const handleTryOther = useCallback(() => {
    if (!via) return;
    const otherVia: Via = via === 'dt5' ? 'dt6' : 'dt5';
    setVia(otherVia);
    setAnswers({});
    setResult(null);
    setScreen('quiz');
  }, [via]);

  return (
    <div
      className="min-h-screen"
      dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
      style={{
        background: 'linear-gradient(135deg, #faf6f0 0%, #e8f5e9 100%)',
      }}
    >
      <div className={container}>
        <RegHeader />

        <div className="space-y-6">
          {screen === 'intro' && <RegIntro onStart={() => setScreen('via')} />}

          {screen === 'via' && <ViaSelector onSelect={handleSelectVia} />}

          {screen === 'quiz' && via && (
            <Quiz via={via} onComplete={handleQuizComplete} onBack={handleBackToVia} />
          )}

          {screen === 'results' && result && via && (
            <>
              <QuizResults result={result} />
              <LeadContactForm via={via} eligible={result.eligible} answers={answers} />

              <div className="flex gap-3 max-sm:flex-col justify-center">
                <button
                  onClick={handleNewEval}
                  className={btnSecondary}
                  style={{ fontFamily: REG_FONTS.body }}
                >
                  {t('results.newEval')}
                </button>
                <button
                  onClick={handleTryOther}
                  className={btnSecondary}
                  style={{ fontFamily: REG_FONTS.body }}
                >
                  {t('results.tryOther')}
                </button>
              </div>
            </>
          )}

          <TrustBadges />
        </div>
      </div>
    </div>
  );
}
